import DeleteIcon from "@root/assets/icons/delete-02.svg";
import EditIcon from "@root/assets/icons/pencil.svg";
import { Flex, Image, Text, Box, IconButton } from "@chakra-ui/react";
import { DATE_FORMATS, DELETE, EDIT, NewsPaperType } from "@root/constants";
import { UpRightArrowIcon } from "../../../assets/admin/icons/arrowicon";
import { useTranslation } from "react-i18next";
import { formatDate, formatDateToISO } from "@root/utils/date-helper";
import { useGlobalContext } from "@root/context/global-context";
import { useState } from "react";
import { ChangeHistoryIcon } from "@root/assets/icons/historyIcon";

export const TableColumns = (
  handleActions: any,
  navigate: any,
  handleChangeHistoryClick: any
) => {
  const { t } = useTranslation("common");

  return [
    {
      header: t("subscriberManagement.userid"),
      accessorKey: "userid",
      size: 60,
      minSize: 50,
      cell: (info: any) => (
        <Text fontSize="12px" color="#2F2F34" whiteSpace="nowrap">
          {info?.row?.original?.id}
        </Text>
      ),
    },
    {
      header: t("subscriberManagement.adminType", "Admin Type"),
      accessorKey: "adminType",
      size: 120,
      minSize: 100,
      cell: (info: any) => {
        const role = info?.row?.original?.role;
        const isSubscriber = role === 3;
        const isNonSubscriber = role === 4;
        const label = isSubscriber
          ? t("signup.subscriber", "Subscriber")
          : isNonSubscriber
          ? t("signup.nonSubscriber", "Non-Subscriber")
          : "-";
        const bg = isSubscriber ? "#EEF2FF" : isNonSubscriber ? "#FFF7ED" : "#F3F4F6";
        const color = isSubscriber ? "#4F46E5" : isNonSubscriber ? "#C2410C" : "#6B7280";

        return (
          <Flex
            px="10px"
            py="4px"
            bg={bg}
            color={color}
            fontWeight="600"
            fontSize="12px"
            borderRadius="16px"
            justify="center"
            align="center"
            w="fit-content"
            minW="80px"
            h="24px"
            whiteSpace="nowrap"
          >
            {label}
          </Flex>
        );
      },
    },
    {
      header: t("subscriberManagement.subscriber"),
      accessorKey: "subscriber",
      size: 130,
      minSize: 100,
      cell: (info: any) => (
        <Text
          fontWeight="500"
          fontSize="12px"
          color="#2F2F34"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          maxW="130px"
          title={info?.row?.original?.companyName}
        >
          {info?.row?.original?.companyName || "N/A"}
        </Text>
      ),
    },
    {
      header: t("subscriberManagement.administrator"),
      accessorKey: "fullName",
      size: 200,
      minSize: 160,
      sortingFn: (rowA: any, rowB: any) => {
        const nameA = rowA.original?.fullName || "";
        const nameB = rowB.original?.fullName || "";
        return nameA.localeCompare(nameB, "ko", { sensitivity: "accent" });
      },
      cell: (info: any) => {
        return (
          <Box lineHeight="1.3" maxW="200px">
            <Text
              fontWeight="600"
              fontSize="12px"
              color="#2F2F34"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              title={info?.row?.original?.fullName}
            >
              {info?.row?.original?.fullName || "N/A"}
            </Text>
            <Text
              fontSize="11px"
              color="#7B7B7B"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              title={info?.row?.original?.email || info?.row?.original?.loginId}
            >
              {info?.row?.original?.email || info?.row?.original?.loginId || "N/A"}
            </Text>
          </Box>
        );
      },
    },
    {
      header: t("subscriberManagement.registeredCourses"),
      size: 120,
      minSize: 100,
      cell: (info: any) => {
        const value = info?.row?.original?.registeredCourses ?? 0;
        const userid = info?.row?.original?.id;

        return (
          <Flex
            px="8px"
            py="4px"
            w="fit-content"
            minW="45px"
            h="24px"
            align="center"
            justify="center"
            borderRadius="16px"
            bg="#EEF2FF"
            color="#4F46E5"
            fontSize="12px"
            fontWeight="600"
            lineHeight="1"
            cursor="pointer"
            whiteSpace="nowrap"
            onClick={() =>
              navigate(`/admin/subscriber/${userid}/courses`, {
                state: { companyName: info?.row?.original?.companyName },
              })
            }
            _hover={{ opacity: 0.85 }}
          >
            {value}
            <UpRightArrowIcon boxSize={3} color="#4F46E5" ml="4px" />
          </Flex>
        );
      },
    },
    {
      header: t("subscriberManagement.members"),
      size: 80,
      minSize: 70,
      cell: (info: any) => {
        const value = info?.row?.original?.members ?? 0;
        const userid = info?.row?.original?.id;

        return (
          <Flex
            px="8px"
            py="4px"
            bg="#EDFCF2"
            color="#16A34A"
            fontWeight="600"
            fontSize="12px"
            borderRadius="16px"
            justify="center"
            align="center"
            w="fit-content"
            minW="45px"
            h="24px"
            cursor="pointer"
            whiteSpace="nowrap"
            _hover={{ opacity: 0.85 }}
            onClick={() =>
              navigate(`/admin/subscriber/${userid}/members`, {
                state: { companyName: info?.row?.original?.companyName },
              })
            }
          >
            {value}
            <UpRightArrowIcon boxSize={3} color="#16A34A" ml="4px" />
          </Flex>
        );
      },
    },
    {
      header: t("subscriberManagement.subscriberType"),
      size: 110,
      minSize: 95,
      cell: (info: any) => {
        const newsPaperType = info?.row?.original?.newsPaperType;

        const getNewsPaperType = () => {
          if (newsPaperType == NewsPaperType.Daily) return "Daily";
          if (newsPaperType == NewsPaperType.Weekly) return "Weekly";
          return "-";
        };

        const value = getNewsPaperType();

        return (
          <Flex
            px="10px"
            py="4px"
            bg="#F0F9FF"
            color="#026AA2"
            fontWeight="600"
            fontSize="12px"
            borderRadius="16px"
            justify="center"
            align="center"
            w="fit-content"
            minW="65px"
            h="24px"
            whiteSpace="nowrap"
            _hover={{ opacity: 0.85 }}
          >
            {value}
            <UpRightArrowIcon boxSize={3} color="#026AA2" ml="4px" />
          </Flex>
        );
      },
    },
    {
      header: t("journalistRequests.viewHistory"),
      accessorKey: "viewHistory",
      size: 100,
      minSize: 85,
      cell: (info: any) => (
        <Flex justify="center" align="center">
          <IconButton
            aria-label="View History"
            icon={<ChangeHistoryIcon />}
            size="sm"
            minW="32px"
            h="32px"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleChangeHistoryClick(info?.row?.original?.id);
            }}
          />
        </Flex>
      ),
    },
    {
      header: t("subscriberManagement.registrationDate"),
      accessorKey: "registrationDate",
      size: 120,
      minSize: 105,
      cell: (info: any) => (
        <Text fontSize="12px" color="#2F2F34" whiteSpace="nowrap">
          {formatDateToISO(info?.row?.original?.createdAt)}
        </Text>
      ),
    },
    {
      header: t("subscriberManagement.lastLogin"),
      accessorKey: "lastLogin",
      size: 160,
      minSize: 140,
      cell: (info: any) => (
        <Text fontSize="12px" color="#2F2F34" whiteSpace="nowrap">
          {info?.row?.original?.lastLogin
            ? formatDate(
                info?.row?.original?.lastLogin,
                DATE_FORMATS.SUBSCRIPTION_END_DATE
              )
            : "Not logged in yet"}
        </Text>
      ),
    },
    {
      header: t("subscriberManagement.actions"),
      size: 70,
      minSize: 60,
      cell: (info: any) => (
        <Flex gap="8px" align="center" justify="center">
          <Flex
            cursor="pointer"
            p="6px"
            borderRadius="6px"
            _hover={{ bg: "blue.50" }}
            transition="all 0.2s"
            onClick={(e) => {
              e.stopPropagation();
              handleActions(EDIT, info?.row?.original);
            }}
          >
            <Image src={EditIcon} boxSize="16px" />
          </Flex>
          <Flex
            cursor="pointer"
            p="6px"
            borderRadius="6px"
            _hover={{ bg: "gray.100" }}
            transition="all 0.2s"
            onClick={(e) => {
              e.stopPropagation();
              handleActions(DELETE, info?.row?.original);
            }}
          >
            <Image src={DeleteIcon} boxSize="16px" />
          </Flex>
        </Flex>
      ),
    },
  ];
};
